import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X } from 'lucide-react';
import Input from '../input';
import Button from '../button';
import SearchSelect from '../search-select';
import useRequest from '../hooks/use-req';
import { BankAccountsResponse, BankAccount } from '../../pages/recommender/wallet/types';
import { showToast } from '../toast';
import { ClockLoader } from 'react-spinners';
import { AnimatedCheckIcon } from '../animation-check';
import BankAccountPopup from '.';
import BankSelect from '../bank-select';

interface BankAccountPopupProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    walletBalance?: number | undefined;
}

const PayoutPopup: React.FC<BankAccountPopupProps> = ({
    isOpen,
    onClose,
    title = 'Request Payout',
    subtitle = 'Choose a payout method to receive your earnings.',
    walletBalance
}) => {

    const authToken = localStorage.getItem("authToken");
    const [userAccount, setUserAccount] = useState<BankAccountsResponse | null>(null);
    const [success, setSuccess] = useState(false);
    const [successAmount, setSuccessAmount] = useState<number | null>(null);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const handleOpenPopup = () => setIsPopupOpen(true);
    const handleClosePopup = () => setIsPopupOpen(false);

    const { handleSubmit, control, reset, setValue, watch, formState } = useForm({
        mode: 'onChange', // <-- validates on every change
        defaultValues: {
            selectedAccount: '',
            amount: ''
        },
    });

    const amountValue = watch('amount');
    const amountExceedsBalance = walletBalance !== undefined && parseFloat(amountValue || '0') > walletBalance;

    const selectedAccountValue = watch('selectedAccount');
    const selectedAccount = userAccount?.bank_accounts?.find(acc =>
        acc.id.toString() === selectedAccountValue
    );

    const { makeRequest: getUserAccounts } = useRequest("/bank/bank/accounts", "GET", {
        Authorization: `Bearer ${authToken}`,
    });

    const { makeRequest, loading } = useRequest("/payout/payout/initiate", "POST", {
        Authorization: `Bearer ${authToken}`,
    });

    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen) return;

            setLoadingAccounts(true);
            try {
                const [response] = await getUserAccounts();

                if (response?.bank_accounts) {
                    setUserAccount(response);


                    if (response?.default_account) {
                        setValue(
                            "selectedAccount",
                            response?.default_account.id.toString()
                        );
                    } else if (response?.bank_accounts.length > 0) {

                        setValue(
                            "selectedAccount",
                            response?.bank_accounts[0].id.toString()
                        );
                    }
                }
            } catch (error) {
            } finally {
                setLoadingAccounts(false);
            }
        };

        if (isOpen) {
            fetchData();
        }
    }, [isOpen, setValue]);

    const handlePayout = handleSubmit(async (formData) => {
        if (!selectedAccount) {
            showToast("Please select a bank account", false);
            return;
        }

        const amount = Number(formData.amount);


        const payoutData = {
            bank_account_id: selectedAccount.id.toString(),
            amount,
        };

        const [response, error] = await makeRequest(payoutData);


        if (error) {
            const message =
                error?.message ||
                error?.detail ||
                error?.error ||
                "Failed to initiate payout";

            showToast(message, false);

            return;
        }

        if (response?.success) {
            showToast(response?.message || "Payout request received!", true);

            const todayKey = new Date().toDateString();
            const stored = JSON.parse(localStorage.getItem("dailyPayouts") || "{}");
            const todayCount = stored[todayKey] || 0;

            stored[todayKey] = todayCount + 1;
            localStorage.setItem("dailyPayouts", JSON.stringify(stored));

            setSuccess(true);
            setSuccessAmount(amount);
            reset();
        } else {
            showToast(response?.message || "Failed to initiate payout", false);
        }
    });

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSuccessClose = () => {
        setSuccess(false);
        onClose();
        setSuccessAmount(null);
    };

    const accountOptions =
        userAccount?.bank_accounts?.map((account: BankAccount) => ({
            value: account.id.toString(),
            label: `${account.bank_name} - ${account.account_name} (${account.account_number})`,
        })) || [];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex w-full items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">

                {/* X Close button - only shown in normal form */}
                {!success && (
                    <button
                        onClick={handleClose}
                        className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        aria-label="Close popup"
                    >
                        <X size={20} />
                    </button>
                )}

                {success ? (
                    // ─── SUCCESS SCREEN ───
                    <div className="py-8 px-4 w-full text-center">
                        {/* Animated Check Icon */}


                        {/* Header */}
                        <h2 className="text-2xl font-bold text-[#121212] mb-3">
                            Payout Request Received
                        </h2>


                        {/* Message */}
                        <p className="text-[#65799A] text-[14px] mb-8 leading-relaxed">


                            Thanks! We’ve received your payout request of <b> {successAmount?.toLocaleString('en-NG', { minimumFractionDigits: 2 })}</b> It’s now being processed and will be completed within 2–3 business days.
                        </p>


                        <div className="flex justify-center items-center mb-6">
                            <AnimatedCheckIcon />
                        </div>

                        {/* Close Button */}
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-full"
                            type="button"
                            onClick={handleSuccessClose}
                        >
                            Close
                        </Button>
                    </div>
                ) : (
                    // ─── NORMAL PAYOUT FORM ───
                    <>
                        {/* Header */}
                        <div className="mb-6">
                            <h2 className="text-[18px] font-bold text-[#121212]">{title}</h2>
                            <p className="text-[14px] text-[#65799A] font-medium mt-1">{subtitle}</p>
                        </div>

                        <form
                            onSubmit={handlePayout}
                        >
                            {/* Bank Account Selection */}
                            <div className="mb-4">
                                <Controller
                                    name="selectedAccount"
                                    control={control}
                                    rules={{
                                        required: 'Please select a bank account',
                                        validate: value => value !== '' || 'Please select a bank account'
                                    }}
                                    render={({ field, fieldState }) => (
                                        <div>

                                            <BankSelect
                                                {...field}
                                                label="Choose Bank or Payment Service"
                                                options={accountOptions}
                                                className="w-full"
                                                onChange={field.onChange}
                                                value={field.value}
                                            />
                                            {fieldState.error && (
                                                <p className="mt-1 text-sm text-red-600">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>

                            <button className='underline text-[#767994] font-semibold mb-4' type='button' onClick={handleOpenPopup}>
                                Add new account
                            </button>

                            {/* Amount Input */}
                            <Controller
                                name="amount"
                                control={control}
                                rules={{
                                    required: 'Amount is required',
                                    pattern: {
                                        value: /^\d+(\.\d{1,2})?$/,
                                        message: 'Enter a valid amount (e.g., 1000 or 1000.50)',
                                    },
                                    min: {
                                        value: 100,
                                        message: 'Minimum withdrawal amount is ₦100',
                                    },
                                    validate: (value) => {
                                        const amount = parseFloat(value);
                                        if (amount <= 0) return 'Amount must be greater than 0';
                                        if (walletBalance !== undefined && amount > walletBalance)
                                            return 'Amount cannot exceed your wallet balance';
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <div>
                                        <Input
                                            {...field}
                                            type="number"
                                            placeholder="0.00"
                                            className="w-full"
                                            label="How much would you like to withdraw?"
                                            prefix="₦"
                                        />
                                        {fieldState.error && (
                                            <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                                        )}
                                    </div>
                                )}
                            />
                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={loadingAccounts || loading || !selectedAccount || amountExceedsBalance}
                                className="w-full mt-6"
                            >
                                {loading ? (
                                    <ClockLoader color="#ffffff" loading={loading} size={20} />
                                ) : (
                                    'Continue'
                                )}
                            </Button>

                            <p className='text-[#333D4D] text-[12px] text-center mt-4'>Payouts are processed within 2–3 business days. Bank processing fees may apply and are deducted during payout.</p>
                        </form>
                    </>
                )}
            </div>
            <BankAccountPopup
                isOpen={isPopupOpen}
                onClose={handleClosePopup}

            />
        </div>
    );
};

export default PayoutPopup;